import AddIcon from "@mui/icons-material/Add";
import {
	Autocomplete,
	Box,
	createFilterOptions,
	TextField,
} from "@mui/material";
import { useState } from "react";

import {
	formatBilletSize,
	mergeBilletSizes,
	parseBilletSize,
} from "../models/FlowSheet";

const getBilletSizeLabel = (option: number) =>
	option === -1 ? "Default" : formatBilletSize(option);

const filterBilletSizes = createFilterOptions<number>({
	stringify: getBilletSizeLabel,
});

interface BilletSizeSelectorProps {
	billetSizes: number[];
	value: number | null;
	onChange: (billetSize: number | null) => void;
	hidden?: boolean;
}

export const BilletSizeSelector = ({
	billetSizes,
	value,
	onChange,
	hidden = false,
}: BilletSizeSelectorProps) => {
	const [addedBilletSizes, setAddedBilletSizes] = useState<number[]>([]);

	const options = [-1, ...mergeBilletSizes(billetSizes, addedBilletSizes)];

	const selectBilletSize = (selected: number | string) => {
		const size =
			typeof selected === "string" ? parseBilletSize(selected) : selected;
		if (size === null) return;
		if (size === -1) {
			onChange(null);
			return;
		}
		if (!options.includes(size)) {
			setAddedBilletSizes((sizes) => [...sizes, size]);
		}
		onChange(size);
	};

	return (
		<Autocomplete<number, false, true, true>
			freeSolo
			disableClearable
			clearOnBlur
			openOnFocus
			size="small"
			options={options}
			value={value ?? -1}
			getOptionLabel={(option) =>
				typeof option === "number" ? getBilletSizeLabel(option) : option
			}
			filterOptions={(available, state) => {
				const filtered = filterBilletSizes(available, state);
				const typedSize = parseBilletSize(state.inputValue);
				if (typedSize !== null && !available.includes(typedSize)) {
					return [...filtered, typedSize];
				}
				return typedSize === null ? [...filtered, 0] : filtered;
			}}
			getOptionDisabled={(option) => option === 0}
			renderOption={({ key: _key, ...props }, option) =>
				options.includes(option) ? (
					<li key={option} {...props}>
						{getBilletSizeLabel(option)}
					</li>
				) : (
					<Box
						component="li"
						key={option}
						{...props}
						sx={{
							gap: 1,
							borderTop: 1,
							borderColor: "divider",
							color: "primary.main",
							"&.Mui-disabled": { color: "text.secondary" },
						}}
					>
						<AddIcon fontSize="small" />
						{option === 0
							? "Type a size to add it, e.g. 8"
							: `Add ${formatBilletSize(option)}`}
					</Box>
				)
			}
			onChange={(_event, selected) => selectBilletSize(selected)}
			data-test-id="flow-sheet-billet-size-selector"
			sx={{ flexGrow: 1, visibility: hidden ? "hidden" : "visible" }}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Billet size"
					inputProps={{ ...params.inputProps, inputMode: "numeric" }}
				/>
			)}
		/>
	);
};
